import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { listReviews, deleteReview } from '../api/client';
import HeroStage from '../components/HeroStage';

gsap.registerPlugin(ScrollTrigger);

const STATUS_LABEL = { draft: 'Draft', analysing: 'Analysing', complete: 'Complete', failed: 'Failed' };
const STATUS_CLASS = { draft: 'badge-gray', analysing: 'badge-dark', complete: 'badge-green', failed: 'badge-red' };
const TYPE_LABEL = {
  standard_apartment: 'Apartment (VvE)',
  house:              'House',
  erfpacht_apartment: 'Erfpacht Apt.',
  erfpacht_house:     'Erfpacht House',
};

const MARQUEE_ITEMS = [
  'Erfpachtakte', 'Splitsingsakte', 'NVM Vragenlijst', 'MJOP', 'VvE Notulen',
  'Meetbouten', 'Bodeminformatie', 'WOZ-beschikking', 'NEN 2580', 'Energielabel',
  'Leveringsakte', 'Bestemmingsplan', 'BAG Extract', 'Kadastrale Kaart',
];

const prefersReduced = typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function Dashboard() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const root        = useRef(null);
  const marqueeRef  = useRef(null);
  const statRefs    = useRef([]);

  useEffect(() => {
    listReviews().then(setReviews).finally(() => setLoading(false));
  }, []);

  /* Hero line reveal + marquee loop + statement scroll reveal — run once on mount */
  useLayoutEffect(() => {
    if (prefersReduced) return;
    const ctx = gsap.context(() => {
      gsap.fromTo('.hero-head .line-mask > span',
        { yPercent: 112 },
        { yPercent: 0, duration: 0.9, ease: 'power3.out', stagger: 0.12, delay: 0.1 },
      );
      // subtle parallax drift of the headline as the hero scrolls away
      gsap.to('.hero-head', {
        yPercent: 10, ease: 'none',
        scrollTrigger: { trigger: '.hero-band', start: 'top top', end: 'bottom top', scrub: true },
      });
      // statement lines reveal on scroll
      gsap.fromTo('.statement .line-mask > span',
        { yPercent: 112 },
        {
          yPercent: 0, duration: 0.85, ease: 'power3.out', stagger: 0.1,
          scrollTrigger: { trigger: '.statement', start: 'top 82%', once: true },
        },
      );
      if (marqueeRef.current) {
        gsap.to(marqueeRef.current, {
          xPercent: -50, ease: 'none', duration: 36, repeat: -1,
        });
      }
    }, root);
    return () => ctx.revert();
  }, []);

  /* Stat counters + portfolio row reveal — run when data arrives */
  useLayoutEffect(() => {
    if (loading) return;
    const ctx = gsap.context(() => {
      // counters
      statRefs.current.forEach(el => {
        if (!el) return;
        const target = parseInt(el.dataset.target || '0', 10);
        if (prefersReduced) { el.textContent = target; return; }
        const obj = { v: 0 };
        gsap.to(obj, {
          v: target, duration: 1.4, ease: 'power2.out',
          snap: { v: 1 },
          onUpdate: () => { el.textContent = Math.floor(obj.v); },
          scrollTrigger: { trigger: el, start: 'top 92%', once: true },
        });
      });
      // portfolio rows
      if (!prefersReduced) {
        gsap.utils.toArray('.plist-row').forEach((row, i) => {
          gsap.from(row, {
            opacity: 0, y: 28, duration: 0.6, ease: 'power2.out', delay: (i % 8) * 0.05,
            scrollTrigger: { trigger: row, start: 'top 94%', once: true },
          });
        });
        gsap.from('.sec-reveal', {
          opacity: 0, y: 20, duration: 0.6, ease: 'power2.out',
          scrollTrigger: { trigger: '.sec-reveal', start: 'top 90%', once: true },
        });
      }
      ScrollTrigger.refresh();
    }, root);
    return () => ctx.revert();
  }, [loading, reviews.length]);

  async function handleDelete(e, id) {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this analysis?')) return;
    await deleteReview(id);
    setReviews(r => r.filter(x => x.id !== id));
  }


  return (
    <div ref={root}>

      {/* ── Hero (HH design system — animated dossier stage) ── */}
      <div className="hero-band" style={{ background: 'var(--cream)', padding: 'clamp(40px, 7vh, 70px) 24px 56px' }}>
        <div className="hero-head dp-hero" style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div className="hero-left">
            <span className="eyebrow" style={{ display: 'inline-flex', alignItems: 'center', gap: 11 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
              Internal review platform
            </span>
            <h1 className="display-xl" style={{ margin: '22px 0 0', maxWidth: 940 }}>
              <span className="line-mask"><span>Every document read.</span></span>
              <span className="line-mask"><span>Every risk <em className="display-accent">surfaced.</em></span></span>
            </h1>
            <p className="dp-lede">
              Upload the seller package and DossierProof reads every page — leasehold to VvE —
              assembling one dossier and surfacing the risks for you to review and sign off.
            </p>
            <div className="dp-cta-row">
              <Link to="/new" style={{ textDecoration: 'none' }}>
                <button className="btn btn-primary btn-dot" style={{ padding: '16px 30px', fontSize: 15 }}>
                  Analyse a Package
                </button>
              </Link>
              <a href="#portfolio" style={{ textDecoration: 'none' }}>
                <button className="btn btn-secondary" style={{ padding: '16px 28px', fontSize: 15 }}>
                  View analyses
                </button>
              </a>
            </div>
          </div>
          <HeroStage />
        </div>
      </div>

      {/* ── Marquee ── */}
      <div style={{ background: 'var(--cream)' }}>
        <div className="marquee">
          <div className="marquee-track" ref={marqueeRef}>
            {[0, 1].map(copy => (
              <span key={copy} style={{ display: 'inline-flex' }}>
                {MARQUEE_ITEMS.map((item, i) => (
                  <span className="marquee-item" key={`${copy}-${i}`}>{item}</span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Statement ── */}
      <section className="statement">
        <div className="eyebrow" style={{ marginBottom: 18 }}>How we work</div>
        <h2 className="display-lg" style={{ maxWidth: 880 }}>
          <span className="line-mask"><span>The AI reads and flags —</span></span>
          <span className="line-mask"><span>an HH agent <em className="display-accent">reviews and signs off</em></span></span>
          <span className="line-mask"><span>before anything reaches a client.</span></span>
        </h2>
      </section>

      {/* ── Portfolio: recent analyses ── */}
      <div id="portfolio" style={{ maxWidth: 1100, margin: '0 auto', padding: '72px 24px 90px' }}>
        <div className="flex-between sec-reveal" style={{ marginBottom: 34, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 12 }}>Workspace</div>
            <h2 className="display-lg">Recent analyses</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: 8 }}>
              {reviews.length} package{reviews.length !== 1 ? 's' : ''} in the HH workspace
            </p>
          </div>
          <Link to="/new" style={{ textDecoration: 'none' }}>
            <button className="btn btn-primary btn-dot btn-sm">New Analysis</button>
          </Link>
        </div>

        {loading && <p className="text-muted">Loading...</p>}

        {!loading && reviews.length === 0 && (
          <div className="card empty-state">
            <div style={{ fontSize: 44, marginBottom: 16 }}>📂</div>
            <h3>No analyses yet</h3>
            <p style={{ marginBottom: 28 }}>Upload a seller package to get started.</p>
            <Link to="/new">
              <button className="btn btn-primary">Analyse a Package</button>
            </Link>
          </div>
        )}

        {!loading && reviews.length > 0 && (
          <div className="plist">
            {reviews.map((review, i) => (
              <div
                key={review.id}
                className="plist-row"
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/reviews/${review.id}`)}
                onKeyDown={e => { if (e.key === 'Enter') navigate(`/reviews/${review.id}`); }}
              >
                <span className="plist-num">{String(i + 1).padStart(2, '0')}</span>
                <span style={{ minWidth: 0 }}>
                  <span className="plist-title">{review.property_address}</span>
                  <span className="plist-meta" style={{ marginTop: 6 }}>
                    <span>{new Date(review.created_at).toLocaleDateString('en-GB')}</span>
                    <span>·</span>
                    <span>{TYPE_LABEL[review.property_type] || review.property_type}</span>
                    <span className={`badge ${STATUS_CLASS[review.status] || 'badge-gray'}`}>
                      {STATUS_LABEL[review.status] || review.status}
                    </span>
                  </span>
                </span>
                <span className="plist-flags">
                  {review.status === 'complete' && review.flag_counts ? (
                    <>
                      <span className="plist-verdict">
                        {(review.flag_counts.red || 0) > 0 ? 'High attention'
                          : (review.flag_counts.amber || 0) > 0 ? 'Moderate'
                          : 'Clear'}
                      </span>
                      <span style={{ color: 'var(--red)' }}>● {review.flag_counts.red || 0}</span>
                      <span style={{ color: '#a96f10' }}>● {review.flag_counts.amber || 0}</span>
                      <span style={{ color: 'var(--green)' }}>● {review.flag_counts.green || 0}</span>
                    </>
                  ) : null}
                </span>
                <button className="plist-del" onClick={e => handleDelete(e, review.id)} title="Delete">
                  ✕
                </button>
                <span className="plist-arrow">→</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
